import { create } from "zustand";

import type { AppLocale } from "@/core/i18n/types";

export type ThemePreference = "light" | "dark" | "system";

export interface NotificationPreferences {
  loginAlerts: boolean;
  adminActions: boolean;
  activityReviewedAt?: string | null;
  dashboardLayout?: string[];
}

export const defaultNotificationPreferences: NotificationPreferences = {
  loginAlerts: true,
  adminActions: true,
};

export interface PreferencesSnapshot {
  locale: AppLocale;
  timezone: string;
  compactMode: boolean;
  reducedMotion: boolean;
  theme: ThemePreference;
  notificationPreferences: NotificationPreferences;
}

interface PreferencesState extends PreferencesSnapshot {
  setPreferences: (patch: Partial<PreferencesSnapshot>) => void;
  hydrateFromServer: (prefs: Record<string, unknown>) => void;
}

const THEME_KEY = "owo:theme";
const LOCALE_KEY = "owo:locale";

function readTheme(): ThemePreference {
  const saved = globalThis.localStorage?.getItem(THEME_KEY);
  return saved === "light" || saved === "dark" || saved === "system" ? saved : "system";
}

function isLocale(value: unknown): value is AppLocale {
  return value === "zh-CN" || value === "en";
}

export function browserLocale(languages: readonly string[] = typeof navigator === "undefined" ? [] : navigator.languages?.length ? navigator.languages : [navigator.language]): AppLocale {
  for (const language of languages) {
    const normalized = language.toLowerCase();
    if (normalized.startsWith("zh")) return "zh-CN";
    if (normalized.startsWith("en")) return "en";
  }
  return "en";
}

function readLocale(): AppLocale {
  const saved = globalThis.localStorage?.getItem(LOCALE_KEY);
  return isLocale(saved) ? saved : browserLocale();
}

function isTheme(value: unknown): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

function persistTheme(theme: ThemePreference) {
  if (typeof window === "undefined") return;
  globalThis.localStorage?.setItem(THEME_KEY, theme);
}

export const usePreferences = create<PreferencesState>((set) => ({
  locale: typeof window === "undefined" ? "zh-CN" : readLocale(),
  timezone: "UTC",
  compactMode: false,
  reducedMotion: false,
  theme: typeof window === "undefined" ? "system" : readTheme(),
  notificationPreferences: defaultNotificationPreferences,
  setPreferences: (patch) => set((state) => {
    if (patch.theme) persistTheme(patch.theme);
    if (patch.locale) globalThis.localStorage?.setItem(LOCALE_KEY, patch.locale);
    return { ...state, ...patch };
  }),
  hydrateFromServer: (prefs) => set((state) => {
    const next: PreferencesSnapshot = {
      locale: isLocale(prefs.locale) ? prefs.locale : state.locale,
      timezone: typeof prefs.timezone === "string" && prefs.timezone ? prefs.timezone : state.timezone,
      compactMode: Boolean(prefs.compact_mode),
      reducedMotion: Boolean(prefs.reduced_motion),
      theme: isTheme(prefs.theme) ? prefs.theme : state.theme,
      notificationPreferences: parseNotificationPreferences(prefs.notification_preferences ?? state.notificationPreferences),
    };
    persistTheme(next.theme);
    return next;
  }),
}));

/** Backend stores notification_preferences as JSON bytes and expects base64 on the wire. */
export function preferencesPayload(state: PreferencesSnapshot) {
  return {
    locale: state.locale,
    timezone: state.timezone,
    theme: state.theme,
    compact_mode: state.compactMode,
    reduced_motion: state.reducedMotion,
    notification_preferences: encodeNotificationPreferences(state.notificationPreferences),
  };
}

export function parseNotificationPreferences(value: unknown): NotificationPreferences {
  const parsed = decodeNotificationValue(value);
  if (!parsed) return { ...defaultNotificationPreferences };
  const layout = Array.isArray(parsed.dashboard_layout)
    ? parsed.dashboard_layout.filter((item): item is string => typeof item === "string" && item.length > 0)
    : undefined;
  return {
    loginAlerts: typeof parsed.login_alerts === "boolean" ? parsed.login_alerts : defaultNotificationPreferences.loginAlerts,
    adminActions: typeof parsed.admin_actions === "boolean" ? parsed.admin_actions : defaultNotificationPreferences.adminActions,
    activityReviewedAt: typeof parsed.activity_reviewed_at === "string" && parsed.activity_reviewed_at ? parsed.activity_reviewed_at : null,
    ...(layout ? { dashboardLayout: layout } : {}),
  };
}

export function encodeNotificationPreferences(preferences: NotificationPreferences): string {
  const body: Record<string, unknown> = {
    login_alerts: preferences.loginAlerts,
    admin_actions: preferences.adminActions,
    activity_reviewed_at: preferences.activityReviewedAt ?? null,
  };
  if (preferences.dashboardLayout && preferences.dashboardLayout.length > 0) body.dashboard_layout = preferences.dashboardLayout;
  const json = JSON.stringify(body);
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function decodeNotificationValue(value: unknown): Record<string, unknown> | null {
  if (!value) return null;
  if (typeof value === "object") return value as Record<string, unknown>;
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;
  try {
    const json = text.startsWith("{") ? text : new TextDecoder().decode(Uint8Array.from(atob(text), (char) => char.charCodeAt(0)));
    const parsed = JSON.parse(json) as unknown;
    return parsed && typeof parsed === "object" ? parsed as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

export function resolveTheme(theme: ThemePreference): "light" | "dark" {
  if (theme === "light" || theme === "dark") return theme;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export async function hydrateAccountPreferences(tenant: string, account: (tenant: string, path?: string) => Promise<unknown>) {
  const result = await account(tenant, "/preferences") as { preferences?: Record<string, unknown> };
  if (result.preferences) usePreferences.getState().hydrateFromServer(result.preferences);
}
